import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessSettingsComponentProps } from '@/types/process.types';
import CertificateConfigSection from '@/components/certificate/certificate-config-section';
import { certificateService } from '@/services/certificate/certificate.service';
import type { CertificateTemplate } from '@/types/certificate.types';

const ProcessSettingsComponent: React.FC<ProcessSettingsComponentProps> = ({ form, processData }) => {
  const { t } = useTranslation();
  const [showCertificateConfig, setShowCertificateConfig] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const processId = processData?.id;

  useEffect(() => {
    if (processId) {
      loadCertificateTemplate();
    }
  }, [processId]);

  useEffect(() => {
    const handleFocus = () => {
      if (processId && document.visibilityState === 'visible') {
        loadCertificateTemplate();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [processId]);

  const loadCertificateTemplate = async () => {
    if (!processId) return;

    try {
      setLoadingTemplate(true);
      const template = await certificateService.getTemplateByProcess(processId);
      if (template) {
        setShowCertificateConfig(template.enableCertificateGeneration || false);
      } else {
        setShowCertificateConfig(false);
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Error loading certificate template:', error);
      }
      setShowCertificateConfig(false);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleTemplateChange = useCallback((template: CertificateTemplate | null) => {
    if (template) {
      setShowCertificateConfig(template.enableCertificateGeneration || false);
    }
  }, []);

  return (
    <div className="rounded-lg mb-4">
      <div className="p-4 flex justify-between items-center rounded-[4px] bg-[#001A55] text-white">
        <div className="text-lg font-semibold text-slate-200">
          {processData?.name || t('processManagement.processName')}
        </div>
      </div>
      <div className="px-4 py-6 space-y-3">
        {/* Staff View Forms */}
        <div className="question-container">
          <label htmlFor="staffViewForms" className="main-label !whitespace-normal">
            {t('processManagement.canStaffViewProcessingInformation')}
          </label>
          <select
            id="staffViewForms"
            className="main-input"
            {...form.register('staffViewForms')}
          >
            <option value="" disabled>
              {t('processManagement.chooseAnOption')}
            </option>
            <option value="YES">{t('processManagement.yes')}</option>
            <option value="NO">{t('processManagement.no')}</option>
          </select>
        </div>

        {/* Applicant View Process Level */}
        <div className="question-container">
          <label htmlFor="applicantViewProcessLevel" className="main-label !whitespace-normal">
            {t('processManagement.canApplicantSeeProcessLevel')}
          </label>
          <select
            id="applicantViewProcessLevel"
            className="main-input"
            {...form.register('applicantViewProcessLevel')}
          >
            <option value="" disabled>
              {t('processManagement.chooseAnOption')}
            </option>
            <option value="YES">{t('processManagement.yes')}</option>
            <option value="NO">{t('processManagement.no')}</option>
          </select>
        </div>

        {/* Certificate Configuration */}
        {processId && (
          <div className="question-container">
            <div className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                id="enableCertificateConfig"
                checked={showCertificateConfig}
                onChange={(e) => setShowCertificateConfig(e.target.checked)}
                disabled={loadingTemplate}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label htmlFor="enableCertificateConfig" className="main-label !whitespace-normal cursor-pointer">
                {t('certificate.templateManager.enableAutomaticGeneration')}
              </label>
              {loadingTemplate && (
                <span className="text-xs text-gray-500 ml-2">{t('certificate.templateManager.loadingTemplate')}</span>
              )}
            </div>

            {showCertificateConfig && (
              <CertificateConfigSection
                processId={processId}
                onTemplateChange={handleTemplateChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessSettingsComponent;
